import { useState } from "react"
import { GET_LATEST_POSTS } from "../assets/api/apiCalls"
import Link from "next/link"
import { useRouter } from "next/router"
import { useTranslation } from "next-i18next"
import { useQuery } from "@apollo/client"
import Image from "next/image"
import { SocialLinks } from "./misc/SocialLinks"

export function Footer() {
    const [isSubscribeRequestLoading, setIsSubscribeRequestLoading] = useState(false)
    const [email, setEmail] = useState("")
    const router = useRouter()
    const { t } = useTranslation()
    const { locale } = router.locale ? router : { locale: 'it' }

    const { loading: latestPostsLoading, error: latestPostsError, data: latestPostsData } = useQuery(GET_LATEST_POSTS, {
        nextFetchPolicy: 'cache-first',
        variables: {
            "locale": locale,
            "limit": 4
        }
    })

    const getCurrentYear = () => {
        return new Date().getFullYear()
    }

    const handleNewsletterSubscription = async () => {
        setIsSubscribeRequestLoading(true)
        try {
            const url = "https://api.sender.net/v2/subscribers"
            const apiKey = process.env.NEXT_PUBLIC_SENDER_API_KEY
        
            const data = {
              email: email,
              groups: ["New subscribers"],
              trigger_automation: true,
            };
        
            const headers = {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            }
        
            const response = await fetch(url, {
              method: "POST",
              headers,
              body: JSON.stringify(data),
            })
        
            if (response.status === 200) {
              alert("You have successfully subscribed to my newsletter.")
            } else {
              alert("Failed to subscribe. Please try again.")
            }
          } catch (error) {
            console.error("Error:", error)
        }
        setIsSubscribeRequestLoading(false)
    }
    
    return (
        <div className="relative font-poppins p-[2rem]">
            <div className="max-w-[80rem] mx-auto">
                <div className="flex flex-col justify-center items-center gap-5">
                    <hr className="border-gray-300 w-full h-[2px]"/>
                    <div className="flex flex-col mx-auto justify-center items-center">
                        <div className="mx-auto">
                            <Image
                                src="/assets/logo.jpg"
                                alt="Explorer's Tale logo"
                                width={270}
                                height={10}
                                style={{ objectFit: 'contain' }}
                            />
                        </div>
                        <span className="text-center">{t("common.a_blog_by") + " "}
                            <Link href={{ pathname: "/about"}} className="text-black hover:text-underline">
                                Alessio Lucciola
                            </Link>
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 justify-center gap-3 md:gap-20">
                        <div className="flex flex-col text-center">
                            <p className="font-bold text-xl sm:text-2xl pb-2">{t("post.latest_posts")}</p>
                            {latestPostsData?.posts.data.map((post: any) => (
                                <Link key={post.id} href={{ pathname: "/post/[slug]", query: { slug: post.attributes.slug }}} className="text-black hover:text-underline">
                                    <span>{post.attributes.postTitle}</span>
                                </Link>
                            ))}
                        </div>
                        <div className="flex flex-col text-center">
                            <p className="font-bold text-xl sm:text-2xl pb-2">{t("common.explore")}</p>
                            <Link key="destinations" href={{ pathname: "/destinations"}} className="text-black hover:text-underline">
                                <span>{t("common.destinations")}</span>
                            </Link>
                            <Link key="itineraries" href={{ pathname: "/itineraries"}} className="text-black hover:text-underline">
                                <span>{t("common.itineraries")}</span>
                            </Link>
                            <Link key="traveladvices" href={{ pathname: "/traveladvices"}} className="text-black hover:text-underline">
                                <span>{t("common.travel_advices")}</span>
                            </Link>
                            <Link key="discounts" href={{ pathname: "/discounts"}} className="text-black hover:text-underline">
                                <span>{t("common.discounts")}</span>
                            </Link>
                        </div>
                        <div className="flex flex-col text-center">
                            <p className="font-bold text-xl sm:text-2xl pb-2">{t("common.more")}</p>
                            <div className="flex flex-col text-center">
                                <Link key="about" href={{ pathname: "/about"}} className="text-black hover:text-underline">
                                    <span>{t("common.about")}</span>
                                </Link>
                                <Link key="contacts" href={{ pathname: "/contacts"}} className="text-black hover:text-underline">
                                    <span>{t("common.contacts")}</span>
                                </Link>
                                <Link key="disclaimer" href={{ pathname: "/disclaimer"}} className="text-black hover:text-underline">
                                    <span>Disclaimer</span>
                                </Link>
                                <Link key="cookie_policy" href={{ pathname: "/cookie_policy"}} className="text-black hover:text-underline">
                                    <span>Cookie Policy</span>
                                </Link>
                                <Link key="privacy_policy" href={{ pathname: "/privacy_policy"}} className="text-black hover:text-underline">
                                    <span>Privacy Policy</span>
                                </Link>
                                <Link key="sitemap" href={{ pathname: "/sitemap"}} className="text-black hover:text-underline">
                                    <span>Sitemap</span>
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col text-center max-w-[30rem]">
                        <span className="font-bold text-xl sm:text-2xl pb-2">{t("common.subscribe_newsletter")}</span>
                        <span>{t("common.subscribe_newsletter_description")}</span>
                        <input type="text" placeholder={t("common.insert_email")} className="border border-gray-300 p-2 mt-2 rounded-md w-[100%]" onChange={(e) => setEmail(e.target.value)}/>
                        <button className="bg-primary text-white p-2 mt-2 rounded-md" type="submit" disabled={isSubscribeRequestLoading} onClick={() => handleNewsletterSubscription()}>
                            {isSubscribeRequestLoading ? "Iscrizione in corso" : t("common.subscribe")}
                        </button>
                    </div>
                    <hr className="border-gray-300 w-full h-[2px]"/>
                    <div className="flex flex-col justify-center mx-auto gap-5 items-center md:flex-row md:justify-between md:gap-[3rem] lg:gap-[7rem]">
                        <div className="flex flex-row gap-5 text-3xl pl-0 text-center md:pl-3">
                            {SocialLinks(t)}
                        </div>
                    </div>
                    <div className="flex flex-col text-center">
                        <span>© {getCurrentYear()} Explorer's Tale</span>
                    </div>
                </div>
            </div>
        </div>
    )
}